import { ReactNode } from 'react'
import { LOAN_TYPES, ReportingDocumentStatuses } from '../constants/client'
import { LOCKBOX_OPTIONS } from '../constants/lockboxes'
import { enumNarrow } from '../helpers/enumGuard'
import { IBankAccount, IClientPortalInternalBankAccount } from './bankAccount'
import {
  FinancialsType,
  FundingRequestAmountType,
  FundingRequestStatus,
  IBorrowingBase,
  IClientCollateral,
  IOveradvance,
} from './bbc'
import { ICheck, IWireData } from './collection'
import { IDueDiligenceInfo } from './dueDiligence'
import { AmendmentAnalysisStatus } from './generalAnalysis'
import { IClientEntityRelationship } from './integration'
import { ILegalEntityWaivedByUser } from './legalEntity'
import { FeeType, IFee } from './loanServicing'
import { IParticipation, IParticipationLoanExposure, IParticipationWire } from './participant'
import { IOPSReporting } from './prospects'
import { IRequiredReport } from './requiredReport'
import { ITermLoan } from './termLoan'
import { IUser, IUserContact } from './user'

export interface ClientIntakeStep {
  id: number
  label: string
  description: string
  iconType: 'company' | 'financial' | 'upload'
  activeColor: string
  activeDescription: string
  title: string
  pageDescription: string
  isHtml?: boolean
  onConnectToolsClick?: () => void
}

export const CLIENT_INTAKE_STEPS: ClientIntakeStep[] = [
  {
    id: 0,
    label: 'Company Info',
    description: 'Provide your company details',
    iconType: 'company' as 'company',
    activeColor: '#005239',
    activeDescription:
      'Tell us about your company so we can tailor the experience to your business structure and needs',
    title: 'Tell Us About Your Business',
    pageDescription: '',
  },
  {
    id: 1,
    label: 'Partners & Tools',
    description: 'Add your partners and tools',
    iconType: 'financial' as 'financial',
    activeColor: '#FC6639',
    activeDescription: 'Easily connect your accounting and financial tools',
    title: 'Add Your Partners and Tools',
    pageDescription: 'Share the providers and systems that power your business.',
  },
  {
    id: 2,
    label: 'Upload Documents',
    description: 'Upload any supporting documents',
    iconType: 'upload' as 'upload',
    activeColor: '#0066F5',
    activeDescription:
      "Upload the necessary financial documents to complete your application. Fewer uploads are required if you've connected your tools",
    title: 'Upload Documents',
    pageDescription: 'You’re almost there. Upload required docs or connect your tools.',
  },
]

// The prospect's identity (name, email, phone) is captured once, at password
// setup, so the intake starts at Company Info rather than repeating it.
export enum CLIENT_INTAKE_STEPS_ENUM {
  COMPANY_INFO = 0,
  FINANCIAL_DATA = 1,
  UPLOAD_DOCUMENTS = 2,
}

export enum AccountActivityPeriods {
  Daily = 'Daily',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
  Quarterly = 'Quarterly',
  Annually = 'Annually',
}

export const ACCOUNT_ACTIVITY_PERIOD_OPTIONS = Object.values(AccountActivityPeriods).map(
  (value) => ({
    value,
    label: value,
  }),
)

// Client-portal options (includes the Daily period)
export const ACCOUNT_ACTIVITY_PERIOD_OPTIONS_CLIENT = [
  { value: AccountActivityPeriods.Daily, label: 'Daily' },
  { value: AccountActivityPeriods.Weekly, label: 'Weekly' },
  { value: AccountActivityPeriods.Monthly, label: 'Monthly' },
  { value: AccountActivityPeriods.Quarterly, label: 'Quarterly' },
  { value: AccountActivityPeriods.Annually, label: 'Annually' },
]

export interface Step {
  id: number
  label: string
  description: string
  iconType: 'company' | 'financial' | 'upload'
  activeColor: string
  activeDescription: string
  title: string
  pageDescription: string
}

export enum ClientERPSystem {
  Netsuite = 'Oracle Netsuite',
  QuickbooksOnline = 'Quickbooks Online',
  QuickbooksDesktop = 'Quickbooks Desktop',
  Other = 'Other(s)',
}

export const SALESFORCE_ERP_SYSTEM_ACCOUNT_NAMES_MAPPING = {
  [ClientERPSystem.Netsuite]: 'NetSuite',
  [ClientERPSystem.QuickbooksOnline]: 'Quickbooks',
  [ClientERPSystem.QuickbooksDesktop]: 'Quickbooks',
}

export const SALESFORCE_ERP_SYSTEM_ACCOUNT_TYPES_MAPPING = {
  [ClientERPSystem.QuickbooksOnline]: {
    type: 'SaaS/Tech',
    subType: 'ERP',
  },
}

export const ERP_SYSTEM_RELATIONSHIP_SALESFORCE_DESCRIPTION: Partial<
  Record<ClientInfoStatus, string>
> = {
  Prospect: 'Added from Client Intake: ERP System',
  'Due Diligence': 'Added from Underwriting application: ERP System',
}

export enum ClientInfoStatus {
  Active = 'Active',
  Prospect = 'Prospect',
  DueDiligence = 'Due Diligence',
  Past = 'Past',
  PastArchived = 'Past / Archived',
  Archived = 'Prospect / Archived',
  Current = 'Current',
}

/**
 * Display names the Salesforce sync writes to `organization.record_type`
 * (ACCOUNT_RECORD_TYPE_NAME_BY_ID in SalesforceSync/config.ts). Only these four
 * Account RecordTypes are mapped; every other Account type lands with
 * record_type NULL, so a NULL is not evidence of any particular type.
 *
 * Not to be confused with CrmOrganizationRelationshipType, which shares three of
 * these names but describes a different column: that one bounds the relationship
 * types a user can pick (`organization.type`), this one is the Account RecordType.
 */
export enum SalesforceAccountRecordType {
  Client = 'Client',
  ReferralSource = 'Referral Source',
  Vendor = 'Vendor',
  CurrentLender = 'Current Lender',
}

export enum CodatSyncStatus {
  Started = 'Started',
  Success = 'Success',
  Failed = 'Failed',
}

export const CLIENT_LOAN_BALANCE_STATUSES = [
  ClientInfoStatus.Current,
  ClientInfoStatus.Past,
  ClientInfoStatus.PastArchived,
]

/** The client statuses the pipeline owns. Current, Past and Past / Archived belong to Portfolio. */
export const PRE_FUNDING_CLIENT_STATUSES = [
  ClientInfoStatus.Prospect,
  ClientInfoStatus.DueDiligence,
  ClientInfoStatus.Archived, // 'Prospect / Archived'
] as const

export type PreFundingClientStatus = (typeof PRE_FUNDING_CLIENT_STATUSES)[number]

export const isPreFundingClientStatus = (
  status?: ClientInfoStatus | null,
): status is PreFundingClientStatus =>
  Boolean(status && (PRE_FUNDING_CLIENT_STATUSES as readonly ClientInfoStatus[]).includes(status))

// Statuses for which a company is still in (or archived out of) the Prospect stage and may
// have new term sheets created. Once a company moves past Prospect (Due Diligence, Active, etc.)
// it has 'completed' that phase — existing term sheets can be viewed, but no new ones created.
export const PROSPECT_TERM_CREATABLE_STATUSES = [
  ClientInfoStatus.Prospect,
  ClientInfoStatus.Archived, // 'Prospect / Archived'
]

export const LOAN_BALANCE_STATUSES_WITH_DD = [
  ...CLIENT_LOAN_BALANCE_STATUSES,
  ClientInfoStatus.DueDiligence,
]

// A never-funded prospect can be permanently deleted from these; Due Diligence closes its deal first.
export const isDeletableProspectStatus = (status?: string | null): boolean =>
  status === ClientInfoStatus.Prospect || status === ClientInfoStatus.Archived

// Statuses whose client users get the prospect portal (intake, market news,
// contact BDO) instead of the full client portal: every pre-funding status. An
// archived prospect stays in this group so they land on the inactive screen and
// can reactivate.
export const isProspectPortalStatus = (status?: ClientInfoStatus | null): boolean =>
  isPreFundingClientStatus(status)

// A company that has finished its relationship with Dwight. Deliberately does
// NOT include ClientInfoStatus.Archived — that value is 'Prospect / Archived',
// an archived *prospect*, whose users belong in the prospect portal.
export const PAST_CLIENT_STATUSES = [ClientInfoStatus.Past, ClientInfoStatus.PastArchived]

export enum SalesforceStage {
  Rejected = 'Rejected',
  Archived = 'Archived',
  TrackingEarly = 'Tracking-Too Early',
  ClosedLost = 'Closed Lost',
  ClosedWon = 'Closed Won',
  Dead = 'Dead',
  DueDiligence = 'Due Diligence',
  Funding = 'Funding',
  ContractNegotiation = 'Contract Negotiation',
  Closed = 'Closed',
  Convert = 'Convert',
  TermSheetNegotiation = 'Term Sheet Negotiation',
  OpsCall = 'OPS Call',
  Submission = 'Committee Submission',
  Passed = 'Passed',
  IntroOrMention = 'Intro or Mention',
  GatheringInfo = 'Gathering Info',
  QualifyingCall = 'Qualifying Call',
}

export interface IClientIntakeInfo {
  id: string
  clientName: string
  fullLegalName: string | null
  employeeCount: number | null
  billingPostalCode: string | null
  billingState: string | null
  billingCity: string | null
  billingCountry: string | null
  billingStreet: string | null
  establishedDate: string | null
  phone: string | null
  stateOfFormation: string | null
  federalTaxId: string | null
  primaryContactEmail: string | null
  primaryContactPhone: string | null
  primaryContactFirstName: string | null
  primaryContactLastName: string | null
  erpSystem: ClientERPSystem[]
  erpSystemOther: string | null
}
export interface FormBaseProps {
  onSubmit: (values: Record<string, unknown>) => Promise<void>
  onBack?: () => void
  showBackButton?: boolean
  fullWidthButton?: boolean
  buttonText: string
  validate?: (values: Record<string, unknown>) => object
  initialValues?: object
  children: ReactNode
  bottomContent?: ReactNode
  customDisabled?: boolean
  customDisabledTooltip?: string
  showDummyButton?: boolean
  dummyButtonText?: string
  onDummyClick?: () => void
  isDummyLoading?: boolean
  isSaving?: boolean
  isSaved?: boolean
}
export interface ClientIntakeFormProps {
  onSubmit: (values: Record<string, unknown>) => Promise<void>
  onBack?: () => void
  handleLoadVendors: (
    type: string,
    search: string,
  ) => Promise<{ data: { id: string; name: string }[] }>
  onConnect?: (values: Record<string, unknown>, erpSystems: string[]) => Promise<void>
  buttonText: string
  showBackButton?: boolean
  step: number
  opsReporting?: IOPSReporting
  goToFinancialStep?: () => void
  goToUploadDocumentStep?: () => void
  salesforceId?: string
  uploadDocument?: (id: string, data: FormData) => Promise<void>
  deleteDocument?: (id: string, fileId: string) => void
  saveNotApplicableDocument?: (id: string, data: object) => void
  onIframeStateChange?: (isShowingIframe: boolean) => void
  handleEditResponses?: () => void
  isEditing?: boolean
  putNotification: (notification: object) => void
}

/** A required report as the borrower's own portal renders it. */
export interface IClientPortalRequiredReport {
  id: string
  name?: string
  documentName: string
  isLegalDocument?: boolean | null
  status?: ReportingDocumentStatuses | null
  reportingThoughDate?: string | null
  daysSinceDue?: number | null
  nextDueDate?: string | null
  hasPendingSubmission?: boolean
}

/** Term loans only feed the borrower's total outstanding balance. */
export type IClientPortalTermLoan = Pick<ITermLoan, 'id' | 'loanBalance'>

/**
 * `GET /clients/me`: the borrower's own client as their portal sees it. Kept separate from
 * IClientInfo so a portal screen reading an internal-only field fails to compile.
 */
export interface IClientPortalInfo {
  id: string
  clientName: string
  iconUrl?: string | null
  clientStatus: ClientInfoStatus
  loanType: string | null
  maxLineAmount?: number | null
  isErpConnected?: boolean | null
  erpName: ClientERPSystem | null
  isDirectQboEnabled?: boolean | null
  bbcDocuments: IClientPortalRequiredReport[]
  ongoingReportingDocuments: IClientPortalRequiredReport[]
  lastBBC?: string
  currentAmount?: number | null
  primaryManager: IUserContact | null
  termLoans: IClientPortalTermLoan[]
  codatLink?: string | null
  codatId?: string | null
  isReportingOverdue: boolean
  contractDate?: string | null
  lockbox?: number | null
  bankAccountInfos: IClientPortalInternalBankAccount[]
}

export interface IClientInfo {
  id: string
  accountId?: string
  leadId?: string
  opportunityId?: string
  salesforceLink?: string
  clientName: string
  contractDate?: string
  maturityDate?: string
  industry?: string
  contractDocumentsFolderId?: string
  loanType: string
  pastInvoiceThreshold: number
  crossAge: number
  terminationStartDate?: string
  terminationYear1: number
  terminationYear2: number
  terminationOngoing: number
  clientStatus: ClientInfoStatus
  isPast?: boolean
  lastBBC?: string
  lastBBCNotify?: boolean
  recentFinancials?: string
  recentFinancialsNotify?: boolean
  recentTransactions?: string
  recentTransactionsNotify?: boolean
  nextUnderwrite?: string
  nextUnderwriteNotify?: boolean
  currentAmount?: number
  totalParticipantExposureAmount?: number
  participantContractualExposure?: number
  participationContractDate?: string
  totalNFE?: number
  termLoans?: ITermLoan[]
  primaryManager?: IUser
  primaryClientContact?: IUser
  maxLineAmount?: number
  minimumLineAmount?: number
  unusedLineFee?: number
  excessLoanAvailability?: string
  risk?: number
  requiredReports?: IRequiredReport[]
  bbcDocuments?: IRequiredReport[]
  minimumInterestEffectiveDate: string
  ongoingReportingDocuments?: IRequiredReport[]
  arAdvance: number
  inventoryAdvanceRateCost: number
  inventoryAdvanceRateNolv: number
  rawMaterialsAdvanceRateCost: number
  sublimit: number
  interestRate?: number
  interestRateBase?: number
  interestRateAbovePrime?: number
  futureInterestRateBase?: number
  futureInterestRateAbovePrime?: number
  futureClientRateChangeDate?: string
  nickname: string
  lockboxFee: number
  wireFee: number
  payoffDate?: string
  payoffAmount?: number
  payoffFees?: IClientPayoffFee[]
  overadvancePriorityOrder?: string[]
  payoffInterest?: number
  payoffInterestMinimum?: number
  payoffInterestTotal?: number
  payoffFloatBalance?: number
  payoffLoanBalance?: number
  applyLockboxFee?: boolean
  overrideTerminationFee?: boolean
  applyServicingFee?: boolean
  overridePayoffInterest?: boolean
  manualPayoffInterestAmount?: number
  terminationFee?: number
  servicingFee?: number
  nolv?: number
  gracePeriod?: number
  lastAnnualAppraisal?: string
  lastAnnualFieldExam?: string
  inventoryConverterTemplateId?: string | null
  inventoryIneligibilityFieldAliases?: IInventoryIneligibilityFieldAliases
  codatId?: string
  isErpConnected?: boolean
  isDirectQboEnabled?: boolean
  codatSyncStatus: CodatSyncStatus | null
  codatSyncedAt: string | null
  erpName: ClientERPSystem
  archivedAt: string

  phone?: string
  iconUrl?: string
  website?: string

  employeeCount?: number

  outsideLegalCounsel?: string

  stateOfFormation?: string

  federalTaxId?: string
  establishedDate?: string
  fullLegalName?: string

  billingPostalCode?: string
  billingState?: string
  billingCity?: string
  billingCountry?: string
  billingStreet?: string
  excluded?: boolean
  codatLink?: string
  isReportingOverdue?: boolean
  totalLoanBalance?: number
  lineTurn?: number
  termLoanBalance?: number
  erpSystem?: ClientERPSystem[]
  erpSystemOther?: string
  clientEntityRelationship?: IClientEntityRelationship[]
  opsReportings?: IOPSReporting[]
  clientIntakeInfo?: IClientIntakeInfo[]
  riskRating?: number
  availableAR?: number
  availableInventory?: number
  firstOPSReportingId?: string
  // Id of the CRM Company record linked to this client (Company.clientInfoId).
  // Populated by clients/ShowInfo; keys the company Overview tab on the client /
  // diligence pages.
  companyId?: string | null

  subIndustry: string
  salesChannels: string
  city: string
  state: string
  loanBalance: { loanBalance: number }

  refreshedAt: string | null
  clientInfoUsers: IUser[]
  ddInfo?: IDueDiligenceInfo

  bankAccountInfos?: IBankAccount[]
  lockbox?: number

  utilization?: number
  netAvailability?: number
  isWorkout?: boolean
}

export interface IClientPayoffFee {
  type: FeeType
  title: string
  amount: number
}

export interface IDashboardMetrics {
  netFundsEmployed: number
  dwightFundedBalance: number
  outstandingTermLoan: number
  totalUtilization: number
  totalMinimumLineAmount: number
  totalFundsEmployed: number
  totalCurrentClients: number
  netLineIncreasesYTD: number
  totalMaxLineAmount: number
  nonClientCashBalance: number
  lineIncreasesYTD?: number
  paidOffMaxLineAmount?: number
}

export interface IDashboardMetricsData {
  data: IDashboardMetrics
}

export interface IClientsAggregation {
  data: IClientInfo[]
  totals: {
    totalItems: number
  }
}

export interface IAccountActivity {
  id: string
  type: string
  clientName: string
  clientInfo?: IClientInfo
  amount: number
  loanBalanceAtRecordDate: number
  loanBalanceEnding: number
  termLoanBalanceAtRecordDate: number
  termLoanBalanceEnding: number
  totalLoanBalanceEnding: number
  description: string | null
  notes: string | null
  recordDate: string
  createdAt: string | null
  appliedAt: string | null
  feeId: string | null
  fee?: IFee
  boxLink?: string
  isEditable?: boolean
  isDeletable?: boolean
  isDeleted?: boolean
  isHistorical?: boolean
  // Dated after the month awaiting close; its running balances are not final until that month locks.
  isPending?: boolean
  borrowingBaseId: string | null
  borrowingBase?: IBorrowingBase
  originalMemo?: string
  entityInfo?: {
    id: string
    name: string
  }
  entityIconUrl?: string
  checkData?: ICheck
  wireData?: IWireData
  termLoanActivityId?: string
}

export interface IAccountActivityData {
  data: IAccountActivity[]
  totals: {
    totalItems: number
  }
}

/**
 * Full record-date span of a client's account activity, bounding the page's date pickers.
 * Fetched once per client from its own endpoint rather than recomputed on every list request.
 */
export interface IAccountActivityDateRange {
  minDate: string | null
  maxDate: string | null
}

export interface IAccountActivityGraphData {
  datasets: {
    label: string
    field: string
    data: number[]
  }[]
  labels: string[]
}

export interface IDeletedItem {
  id: string
  itemDate?: string | null
  recordDate?: string | null
  originalRecordDate?: string | null
  description?: string | null
  credit?: number | null
  debit?: number | null
  isSent?: boolean
  // Raw, un-presented values behind the display columns above — what the edit form binds to.
  amount?: number | null
  descriptionText?: string | null
  receivedDate?: string | null
  returnedDate?: string | null
  // Open shape: the deleted-collections FieldArray passes through additional dynamic fields.
  [key: string]: unknown
}

export interface IDeletedItems {
  data: IDeletedItem[]
  totals: {
    totalItems: number
  }
}

export interface IClientCompliance {
  id: string
  clientId: string
  clientName: string
  clientNameSort?: string
  clientNameUrl?: string
  clientStatus: ClientInfoStatus
  loanType: string
  clientBalanceSheet: string
  clientBalanceSheetNextDueDate: string | null
  clientBalanceSheetStatus: ReportingDocumentStatuses | null
  clientBalanceSheetStatusLabel: string
  clientBalanceSheetDaysSinceDue: number
  clientIncomeStatement: string
  clientIncomeStatementNextDueDate: string | null
  clientIncomeStatementStatus: ReportingDocumentStatuses | null
  clientIncomeStatementStatusLabel: string
  clientFinancials: string
  clientFinancialsReporting: string
  clientFinancialsReportingAsOfDate: string
  latestClientFinancialsReportingId: string | null
  latestClientFinancialsReportingAsOfDate: string
  bankTransactions: string
  bankTransactionsNextDueDate: string | null
  bankTransactionsStatus: ReportingDocumentStatuses | null
  bankTransactionsStatusLabel: string
  bankTransactionsReporting: string
  bankTransactionsReportingAsOfDate: string
  bankTransactionsDaysSinceDue: number
  latestBankTransactionsReportingId: string | null
  latestBankTransactionsReportingAsOfDate: string
  projections: string
  projectionsNextDueDate: string | null
  projectionsStatus: ReportingDocumentStatuses | null
  projectionsStatusLabel: string
  projectionsReporting: string
  projectionsReportingAsOfDate: string
  projectionsDaysSinceDue: number
  latestProjectionsReportingId: string | null
  latestProjectionsReportingAsOfDate: string
  salesBySKU: string
  salesBySKUNextDueDate: string | null
  salesBySKUStatus: ReportingDocumentStatuses | null
  salesBySKUStatusLabel: string
  salesBySKUReporting: string
  salesBySKUReportingAsOfDate: string
  salesBySKUDaysSinceDue: number
  latestSalesBySKUReportingId: string | null
  latestSalesBySKUReportingAsOfDate: string
  arLedger: string
  arLedgerNextDueDate: string | null
  arLedgerStatus: ReportingDocumentStatuses | null
  arLedgerStatusLabel: string
  arLedgerReporting: string
  arLedgerReportingAsOfDate: string
  arLedgerDaysSinceDue: number
  latestArLedgerReportingId: string | null
  latestArLedgerReportingAsOfDate: string
  capTable: string
  capTableNextDueDate: string | null
  capTableStatus: ReportingDocumentStatuses | null
  capTableStatusLabel: string
  capTableReporting: string
  capTableReportingAsOfDate: string
  capTableDaysSinceDue: number
  latestCapTableReportingId: string | null
  latestCapTableReportingAsOfDate: string
  coiExpiration: string
  coiExpirationStatus: ReportingDocumentStatuses | null
  coiExpirationStatusLabel: string
  coiExpirationBoxLink: string
  coiReporting: string | null
  coiReportingAsOfDate: string
  eopExpiration: string
  eopExpirationStatus: ReportingDocumentStatuses | null
  eopExpirationStatusLabel: string
  eopExpirationBoxLink: string
  eopReporting: string | null
  eopReportingAsOfDate: string
  borrowingBaseId: string | null
  borrowingBase: string
  funding: string
  borrowingBaseStatus: ReportingDocumentStatuses | null
  borrowingBaseStatusLabel: string
  excessLoanAvailability: number
  requiredReports: string[]
  requiredReportsConfig: {
    documentName: string
    frequency: string[]
    dueDate: string | null
    isLegalDocument?: boolean
  }[]
  primaryUserEmail: string
  primaryUserFirstName: string
  isErpConnected: boolean
  erpName: ClientERPSystem
  complianceNote: string
  totalOverdueReports: number
  listOfClientOverdueReports: string[]
  lastContactedCompliance: string
  currentAmount?: number
  currentAmountString?: string
  risk?: number
  riskString?: string
  lineTurn?: number
  lineTurnString?: string
  refreshedAt: string | null
  contractDate: string | null
  warehouseWaiverCount: number
  totalEligibleWarehouses: number
  warehouseWaiverPercentage: number
  warehouseWaiver: string
  warehouseWaiverStatus: ReportingDocumentStatuses | null
  warehouseWaiverStatusLabel: string
  uccReportDate: string | null
  uccCompletedAtStatus: ReportingDocumentStatuses | null
  uccCompletedAtStatusLabel: string
  uccNextDueDate: string | null
  uccDaysSinceDue: number
  uccBoxLink: string | null
  uccReporting: string | null
  uccReportingAsOfDate: string
  gsReportDate: string | null
  gsCompletedAtStatus: ReportingDocumentStatuses | null
  gsCompletedAtStatusLabel: string
  gsNextDueDate: string | null
  gsDaysSinceDue: number
  gsBoxLink: string | null
  gsReporting: string | null
  gsReportingAsOfDate: string
  lsaReportDate?: string | null
  boardConsentReportDate?: string | null
  ipsaReportDate?: string | null
  noticeOfIpsaReportDate?: string | null
  lienFilingsReportDate?: string | null
  corporateGuaranteesAndPledgesReportDate?: string | null
  supportGuaranteeReportDate?: string | null
  subordinationAgreementsReportDate?: string | null
  warehouseWaiversReportDate?: string | null
  signatureAuthsReportDate?: string | null
  lsaBoxLink?: string | null
  boardConsentBoxLink?: string | null
  ipsaBoxLink?: string | null
  noticeOfIpsaBoxLink?: string | null
  lienFilingsBoxLink?: string | null
  corporateGuaranteesAndPledgesBoxLink?: string | null
  supportGuaranteeBoxLink?: string | null
  subordinationAgreementsBoxLink?: string | null
  signatureAuthsBoxLink?: string | null
  coiReportDateLegal?: string | null
  eopReportDateLegal?: string | null

  /** Entity x state combinations requiring a UCC search; GS excludes per-state GS waivers. */
  entityDetailsTotal?: number
  entityDetailsGsTotal?: number
  entityDetailsUccWithDoc?: number
  entityDetailsUccWithCompliantDoc?: number
  entityDetailsGsWithDoc?: number
  entityDetailsGsWithCompliantDoc?: number
  uccWorstEntityReportDate?: string | null
  gsWorstEntityReportDate?: string | null

  warehouseWaiversTotal?: number
  warehouseWaiversWithWaiver?: number

  miscLegalCorporateGuarantees?: number
  miscLegalSupportGuarantee?: number
  miscLegalSubordinationAgreements?: number

  legalStatusByDocumentType?: Record<
    string,
    {
      status: ReportingDocumentStatuses | null
      statusLabel: string
    }
  >
}

export interface IClientComplianceWithDynamicStatus extends IClientCompliance {
  [key: string]: any
}
export interface IClientComplianceSummaryMetrics {
  clientsInCompliance?: {
    green: number
    red: number
  }
  overdueFinancials?: {
    green: number
    yellow: number
    red: number
  }
  uccStatus?: {
    green: number
    yellow: number
    red: number
  }
  gsStatus?: {
    green: number
    yellow: number
    red: number
  }
  totalReports?: {
    green: number
    yellow: number
    red: number
  }
}

export interface IClientComplianceData {
  data: IClientCompliance[]
  totals: {
    totalItems: number
    refreshedAt: string | null
  }
}

export interface IComplianceStatusRow {
  documentType: string
  documentTypeKey: string
  status: ReportingDocumentStatuses | null
  statusLabel: string
  lastUpdated: string | null
  coverageComplete: boolean
  nextDue: string | null
  details?: string | null
  boxFolderLink?: string | null
  isAdHoc?: boolean
  isLegalDocument?: boolean
  /** Every UCC/GS entity/state has a file, but at least one latest result failed. */
  isNonCompliant?: boolean
}

export interface IComplianceEntityDetailRow {
  entityId: string
  entityName: string
  state: string | null
  lastFiled: string | null
  nextDue: string | null
  status: ReportingDocumentStatuses | null
  daysOverdueLabel: string
  isNonCompliant?: boolean
  /** The latest file's recorded result; set only when it is non-compliant. */
  complianceResult?: string | null
  isWaived?: boolean
  /** Set on per-state document waivers only; absent when the whole entity is waived. */
  waiverId?: string
  waivedNote?: string | null
  waivedAt?: string | null
  waivedByUser?: ILegalEntityWaivedByUser | null
}

export interface IInventoryLocationOption {
  id: string
  location?: string
  mappedLocation?: string
}

export const CLIENT_FOLDER_NAMES = {
  documents: 'Documents',
  underwriting: 'Underwriting',
  preUnderwriting: 'Pre-Underwriting',
  amendments: 'Executed Documents',
  lcr: 'LCR, UW Questions, Detailed Search',
  lsa: 'Executed Documents',
  legalDocuments: 'Legal Documents',
  warehouseWaivers: 'Landlord Waivers',
  ucc: 'Lien Searches _,Termination _,Filing, Search to Reflect, Good Standing Certificate',
  insurance: 'Company Insurance',
  payoff: 'Payoff',
  qboDocuments: 'QuickBooks Documents',
  qboDocumentsAdmin: 'QBO Documents',
}

export interface IAmendmentDetail {
  id: string
  clientName: string
  termName: string
  oldValue: string
  newValue: string
}

export interface IAmendment {
  id: string
  clientName: string
  createdAt: string
  // Null while a Pending amendment from the analysis flow is still awaiting
  // the Executed transition (which captures the date in the modal). Filled in
  // before the apply cron picks the row up.
  effectiveDate: string | null
  amendmentType: ClientAmendmentType
  boxLink: string
  status: ClientAmendmentStatus
  clientAmendmentDetails: IAmendmentDetail[]
  participation: IParticipation
  participant: string
  summaryOfChanges?: string
  isTermChange: boolean
  isManual: boolean
  /** ABL amendment fee, charged on the effective date when the analysis is Executed. */
  fee?: number | null
  overadvanceId?: string
  overadvance?: IOveradvance
  termLoanId?: string
  termLoan?: ITermLoan
  /**
   * Set on the list response for every non-manual amendment — surfaces the
   * status of the analysis that owns it. Used to gate the Delete button
   * (allowed only once the analysis is Archived) and the Box icon's fallback
   * to the analysis's Box folder.
   */
  linkedAnalysisStatus?: AmendmentAnalysisStatus | null
  /** Box file id of the signed amendment filed through the owning analysis, if any. */
  signedAmendmentFileId?: string | null
  /**
   * Id of the amendment-analysis that owns this row (resolved across the
   * ABL / term-loan / overadvance subject FK), or `null` when there is none.
   * Lets the UI deep-link the Amendment Type text into the analysis workspace
   * on every row that has one, and open the analysis's Box folder once it is
   * complete. Resolution is per-subject, so repeat term-loan/overadvance
   * amendments all share that entity's analysis.
   */
  analysisId?: string | null
}

export interface IAmendmentsData {
  // `IOveradvance.status` is omitted because it collides with `IAmendment.status`
  // (lifecycle statuses for amendment vs. overadvance never overlap). When this
  // intersection is consumed, `item.status` refers to the amendment.
  amendments: (IAmendment & Omit<IOveradvance, 'status'>)[]
  totalItems: number
}

export interface IInventoryIneligibilityFieldAliases {
  custom_1?: string
  custom_2?: string
  custom_3?: string
  custom_4?: string
  custom_5?: string
}
export enum TermLoanStatus {
  // Created by an in-progress amendment analysis. Excluded from balance,
  // fee, schedule and UI flows until the analysis transitions to Executed
  // (UpdateStatus flips Draft → Active/Future based on closingDate then).
  Draft = 'Draft',
  Future = 'Future',
  Active = 'Active',
  Terminated = 'Terminated',
}

/** New column on `overadvances`. See migration `1780458379910-amendment-analysis-schema`. */
export enum OveradvanceStatus {
  Draft = 'Draft',
  Active = 'Active',
}

export enum ClientSetupSections {
  LineUtilization = 'Line utilization',
  Collateral = 'Collateral',
  InterestRate = 'Interest rate',
  Fees = 'Fees',
  AdministrationInfo = 'Administration info',
  Reporting = 'Reporting',
}

export interface ClientTerms {
  id: string
  boxLink?: string
  effectiveDate: string
  restartTerm?: boolean
  loanType?: string
  contractDate?: string
  maturityDate?: string
  autoRenew?: string
  lockbox?: number
  wireFee?: number
  lockboxFee?: number
  facilityFee?: number
  terminationStartDate?: string
  terminationYear1?: number
  terminationYear2?: number
  terminationOngoing?: number
  interestRateBase?: number
  interestRateAbovePrime?: number
  interestPerDays?: number
  interestFloatDays?: number
  contractualFloatDays?: number
  nolv?: number
  arAdvance?: number
  inventoryAdvanceRateCost?: number
  rawMaterialsAdvanceRateCost?: number
  inventoryAdvanceRateNolv?: number
  minimumLineAmount?: string
  maxLineAmount?: string
  sublimit?: number
  pastInvoiceThreshold?: number
  crossAge?: number
  unusedLineFee?: number
  nextFacilityFeeDate?: string
  minimumInterestEffectiveDate?: string
  rmNolv?: number
  rmInventoryAdvanceRateNolv?: number
  noticePeriodEnd?: number
  noticePeriodEndDate?: string
  isWorkout?: boolean
  interestRateTiers?: {
    balanceThreshold: number
    interestRateBase: number
    interestRateAbovePrime: number
  }[]
}

export interface OtherClientTerms {
  interestRate?: number
  gracePeriod?: number
  servicingFee?: number
  applyServicingFee?: boolean
}
const PAST_INVOICE_DATE_THRESHOLD_VALUES = ['30', '60', '90']

export enum CollateralSubSection {
  Inventory = 'inventory',
  AR = 'ar',
}

export enum FieldType {
  Select = 'select',
  Number = 'number',
  Date = 'date',
  Percent = 'percent',
  Boolean = 'boolean',
  Currency = 'currency',
  Text = 'text',
}

export interface IField {
  label?: string
  name: string
  type: 'currency' | 'percent' | 'date' | 'number' | 'select' | 'boolean' | 'text' | 'creatable'
  options?: { label: string; value: string | boolean }[]
  disabled?: boolean
  placeholder?: string
  size?: 'small' | 'medium' | 'large'
  minDate?: Date
  maxDate?: Date
  className?: string
  isRequired?: boolean
  displayValue?: string | number
  withClear?: boolean
}

export type ClientSetupField = {
  value: keyof ClientTerms | keyof OtherClientTerms
  dbName?: string
  label: string
  type: FieldType
  section: ClientSetupSections | null
  subSection?: CollateralSubSection
  disabled?: boolean
  options?: { label: string; value: string | boolean }[]
  excludeFromDb?: boolean
  placeholder?: string
  /** A select that can be emptied; the cleared value is saved as null. */
  isClearable?: boolean
}

export const clientTermsInfo: ClientSetupField[] = [
  {
    value: 'effectiveDate',
    label: 'Effective date',
    type: FieldType.Date,
    excludeFromDb: true,
    section: null,
  },
  {
    value: 'maxLineAmount',
    label: 'Max line amount',
    type: FieldType.Currency,
    section: ClientSetupSections.LineUtilization,
  },
  {
    value: 'minimumLineAmount',
    label: 'Min line amount',
    type: FieldType.Currency,
    section: ClientSetupSections.LineUtilization,
  },
  {
    value: 'minimumInterestEffectiveDate',
    label: 'Minimum Interest Start Date',
    type: FieldType.Date,
    section: ClientSetupSections.LineUtilization,
    placeholder: 'Optional',
  },
  {
    value: 'unusedLineFee',
    label: 'Unused line fee',
    type: FieldType.Percent,
    section: ClientSetupSections.LineUtilization,
  },

  {
    value: 'loanType',
    label: 'Deal type',
    type: FieldType.Select,
    section: ClientSetupSections.Collateral,
    options: Object.values(LOAN_TYPES).map((value) => ({
      value,
      label: value,
    })),
  },
  {
    value: 'arAdvance',
    label: 'AR advance',
    type: FieldType.Percent,
    section: ClientSetupSections.Collateral,
    subSection: CollateralSubSection.AR,
  },
  {
    value: 'pastInvoiceThreshold',
    label: 'Past invoice threshold',
    type: FieldType.Number,
    section: ClientSetupSections.Collateral,
    subSection: CollateralSubSection.AR,
    options: PAST_INVOICE_DATE_THRESHOLD_VALUES.map((value) => ({
      value,
      label: value,
    })),
  },
  {
    value: 'crossAge',
    label: 'Cross age',
    type: FieldType.Percent,
    section: ClientSetupSections.Collateral,
    subSection: CollateralSubSection.AR,
  },

  {
    value: 'inventoryAdvanceRateCost',
    label: 'Inventory advance cost',
    type: FieldType.Percent,
    section: ClientSetupSections.Collateral,
    subSection: CollateralSubSection.Inventory,
  },
  {
    value: 'nolv',
    label: 'NOLV',
    type: FieldType.Percent,
    section: ClientSetupSections.Collateral,
    subSection: CollateralSubSection.Inventory,
  },

  {
    value: 'inventoryAdvanceRateNolv',
    label: 'NOLV advance rate',
    type: FieldType.Percent,
    section: ClientSetupSections.Collateral,
    subSection: CollateralSubSection.Inventory,
  },
  {
    value: 'rawMaterialsAdvanceRateCost',
    label: 'Raw materials advance cost',
    type: FieldType.Percent,
    section: ClientSetupSections.Collateral,
    subSection: CollateralSubSection.Inventory,
  },
  {
    value: 'rmNolv',
    label: 'Raw material NOLV',
    type: FieldType.Percent,
    section: ClientSetupSections.Collateral,
    subSection: CollateralSubSection.Inventory,
  },
  {
    value: 'rmInventoryAdvanceRateNolv',
    label: 'Raw material NOLV advance rate',
    type: FieldType.Percent,
    section: ClientSetupSections.Collateral,
    subSection: CollateralSubSection.Inventory,
  },

  {
    value: 'sublimit',
    label: 'Inventory sublimit',
    type: FieldType.Percent,
    section: ClientSetupSections.Collateral,
    subSection: CollateralSubSection.Inventory,
  },
  {
    value: 'isWorkout',
    label: 'Workout',
    type: FieldType.Boolean,
    section: ClientSetupSections.Collateral,
  },
  {
    value: 'interestRateBase',
    label: 'Floor',
    type: FieldType.Percent,
    section: ClientSetupSections.InterestRate,
  },
  {
    value: 'interestRateAbovePrime',
    label: 'Margin above prime',
    type: FieldType.Percent,
    section: ClientSetupSections.InterestRate,
  },
  {
    value: 'interestPerDays',
    label: 'Interest (per days)',
    type: FieldType.Number,
    section: ClientSetupSections.InterestRate,
  },
  {
    value: 'interestFloatDays',
    label: 'Effective float days',
    type: FieldType.Number,
    section: ClientSetupSections.InterestRate,
  },
  {
    value: 'contractualFloatDays',
    label: 'Contractual float days',
    type: FieldType.Number,
    section: ClientSetupSections.InterestRate,
  },

  {
    value: 'wireFee',
    label: 'Wire fee',
    type: FieldType.Currency,
    section: ClientSetupSections.Fees,
  },
  {
    value: 'lockboxFee',
    label: 'Lockbox fee',
    type: FieldType.Currency,
    section: ClientSetupSections.Fees,
  },
  {
    value: 'facilityFee',
    label: 'Facility fee',
    type: FieldType.Percent,
    section: ClientSetupSections.Fees,
  },
  {
    value: 'nextFacilityFeeDate',
    label: 'Next facility fee (after funding)',
    type: FieldType.Date,
    section: ClientSetupSections.Fees,
  },
  {
    value: 'terminationYear1',
    dbName: 'termination_year_1',
    label: 'Termination fee (Year 1)',
    type: FieldType.Percent,
    section: ClientSetupSections.Fees,
  },
  {
    value: 'terminationOngoing',
    dbName: 'termination_ongoing',
    label: 'Termination fee (Ongoing)',
    type: FieldType.Percent,
    section: ClientSetupSections.Fees,
  },
  {
    value: 'terminationYear2',
    dbName: 'termination_year_2',
    label: 'Termination fee (Year 2) (if applicable)',
    type: FieldType.Percent,
    section: ClientSetupSections.Fees,
  },
  {
    value: 'contractDate',
    label: 'Contract date',
    type: FieldType.Date,
    section: ClientSetupSections.AdministrationInfo,
  },
  {
    value: 'terminationStartDate',
    label: 'Term start date',
    type: FieldType.Date,
    section: ClientSetupSections.AdministrationInfo,
    disabled: true,
  },
  {
    value: 'maturityDate',
    label: 'Maturity date',
    type: FieldType.Date,
    section: ClientSetupSections.AdministrationInfo,
  },
  {
    value: 'noticePeriodEnd',
    label: 'Notice before maturity (days)',
    type: FieldType.Number,
    section: ClientSetupSections.AdministrationInfo,
  },
  {
    value: 'noticePeriodEndDate',
    label: 'Last day to give notice',
    type: FieldType.Date,
    section: ClientSetupSections.AdministrationInfo,
    disabled: true,
  },
  {
    value: 'lockbox',
    label: 'Lockbox',
    type: FieldType.Select,
    section: ClientSetupSections.AdministrationInfo,
    options: LOCKBOX_OPTIONS,
    isClearable: true,
  },
  {
    value: 'autoRenew',
    label: 'Auto renew',
    type: FieldType.Select,
    section: ClientSetupSections.AdministrationInfo,
    options: [
      {
        label: 'Yes',
        value: 'true',
      },
      {
        label: 'No',
        value: 'false',
      },
    ],
  },
]

const otherClientTermsInfo: ClientSetupField[] = [
  {
    value: 'interestRate',
    label: 'Rate',
    type: FieldType.Percent,
    disabled: true,
    section: ClientSetupSections.InterestRate,
  },
  {
    value: 'applyServicingFee',
    label: 'Apply servicing fee',
    type: FieldType.Boolean,
    section: ClientSetupSections.Fees,
  },
  {
    value: 'servicingFee',
    label: 'Servicing fee',
    type: FieldType.Currency,
    section: ClientSetupSections.Fees,
  },
  {
    value: 'gracePeriod',
    label: 'Grace period',
    type: FieldType.Number,
    section: ClientSetupSections.Fees,
  },
]

export const covenantsClientTermsInfo = [
  {
    value: 'guarentees',
    label: 'Guarantees',
    type: FieldType.Text,
  },
  {
    value: 'subordination',
    label: 'Subordination language',
    type: FieldType.Boolean,
  },
  {
    value: 'covenantType',
    label: 'Covenant type',
    type: FieldType.Text,
  },
]

export const allClientTermsInfo: ClientSetupField[] = [
  ...otherClientTermsInfo,
  ...clientTermsInfo,
  {
    value: 'interestRateTiers',
    label: 'Interest Rate Tiers',
    dbName: 'interest_rate_tiers',
    type: FieldType.Text,
    section: null,
    excludeFromDb: true,
  },
]

export enum ClientAmendmentStatus {
  // Created by an in-progress amendment analysis. Parked from the apply cron
  // and from anywhere that reads "live" amendments until the analysis
  // transitions to Executed (UpdateStatus flips Draft → Pending then).
  Draft = 'Draft',
  Pending = 'Pending',
  Completed = 'Completed',
  // Stamped by `terminateClient` at payoff: every amendment still Pending
  // when the client pays off is voided, regardless of effective date — its
  // term changes never took effect, so readers must treat it like Draft.
  // Amendments created on an already-Past client afterwards are deliberate
  // and stay on the normal apply pipeline.
  Voided = 'Voided',
  // Stamped when the amendment analysis proposing it is archived: the
  // proposal never reached production. Un-archiving hands it back to Draft.
  Archived = 'Archived',
}

/**
 * Amendments whose term changes never took effect — Draft (analysis still in
 * progress), Voided (client paid off first) and Archived (analysis archived).
 * Every reader of "live" amendments excludes these.
 */
export const INACTIVE_CLIENT_AMENDMENT_STATUSES: ClientAmendmentStatus[] = [
  ClientAmendmentStatus.Draft,
  ClientAmendmentStatus.Voided,
  ClientAmendmentStatus.Archived,
]

export enum ClientAmendmentType {
  Legal = 'Legal',
  NonLegal = 'Non-Legal',
}

// Summary text that marks the synthetic "Initial ABL Structure" origin
// amendment (created at first funding / backfilled). Used to identify the row
// on the client + to keep the backfill migration idempotent.
export const INITIAL_ABL_STRUCTURE_SUMMARY = 'Initial ABL Structure'

export interface WatchlistDetail {
  riskRating: number
  riskRatingChange: number
  percentApLess_60: number
  grossRevenue_6MonthRollingAvg: number
  ebitda_6MonthRollingAvg: number
  grossMarginRollingAvg: number
  yoyNetRevGrowth: number
  percentArLess_60: number
  arTop_5Conc: number
  lineTurn: number
  cashBurn: number
  cashBurnT6m: number
  cashBurnT12m: number
  currentRatio: number
  dilution: number
  weeksOfSupply: number
  inventoryHealthRating: number
}

export interface IRiskRatingSummary {
  id: string
  clientInfo: IClientInfo
  riskRating: number
  lineTurn: number
  cashBurn: number
  cashBurnT6m: number
  cashBurnT12m: number
  grossMarginRollingAvg: number
  percentApLess60: number
  ebitda6MonthRollingAvg: number
  currentRatio: number
  percentArLess60: number
  dilution: number
  arTop5Conc: number
  weeksOfSupply: number
  loanBalance: number
  totalLoanBalance: number
  riskRatingRawData: IRiskRatingSummary
}

export interface IRiskRatingSummaryData {
  data: IRiskRatingSummary[]
  totalItems: number
  dateOptions: { value: string; label: string }[]
}

export interface IRiskRatingSummaryHeaderData {
  watchlistTierCount: number
  lowRiskClientsCount: number
  averageRiskRating: number
  watchlistTierNFE: number
  // ISO timestamp of the last RefreshRiskRating run; null before the first one.
  lastRefreshedAt: string | null
  // Whether a RefreshRiskRating run is in flight right now — sourced from the queue, so it
  // covers an automatic refresh as well as one the user started from the Watchlist.
  isRefreshing: boolean
}

export enum WatchListRiskRatingTier {
  Workout = 'Workout',
  Watchlist = 'Watchlist',
  Monitor = 'Monitor',
}

export interface IRiskRatingDetail extends WatchlistDetail {}

export interface IRiskRatingRawData extends WatchlistDetail {
  avgEbitda: number
  cashAndCashEquivalents: number
  t3mGrossProfit: number
  t3mNetRevenue: number
  trailingSixNetRev: number
  pyTrailingSixNetRev: number
  totalCurrentAssets: number
  totalCurrentLiabilities: number
  t3mGrossRevenue: number
  t12mCogs: number
  inventory: number
  totalGrossAr: number
  ar1To60Days: number
  totalAp: number
  ap0To60Days: number
  averageLoanBalance: number
  averageCollections: number
  inventoryWoS: number
  daysSinceLandedGt180: number
  daysUntilExpirationLt90: number
  salesAndOnHandVariance: number
  maxSalesMonths: number
  previousRiskRating: number
}

export interface IRiskRating {
  data: IRiskRatingRawData
  scores: IRiskRatingDetail
  portfolioScores: IRiskRatingDetail
  portfolioData: IRiskRatingRawData
  date: string
  loanBalance: number
}

export interface IRiskRatingData {
  data: IRiskRating[]
}

export interface IClientFundingsBBC {
  id: string
  recordDate: string
  status: FundingRequestStatus
  requestedAmount: number | null
  requestedAmountType: FundingRequestAmountType
  clientInfo: {
    id: string
    clientName: string
    loanType: string
    inventoryIneligibilityFieldAliases?: Record<string, string>
  }
  clientCollateral: IClientCollateral | null
  projectedNewLoanAmount: number | null
}

export interface IClientBBCsListData {
  data: IClientFundingsBBC[]
  dateRange?: { from: string; to: string }
}

export interface IFinancialsComparison {
  clientName: string
  industry: string
  riskRating: number
  clientId: string
  recordDate: string
  metric1Name: string
  metric1: number
  metric1Projection: number
  metric1Difference: number
  metric1DifferencePercentage: number
  metric1ProjectionTooltip?: string
  metric1ActualsTooltip?: string
  metric1PercentRevenue: number
  metric1ProjectionPercentRevenue: number
  metric1ComparisonBaseActuals: number
  metric1ComparisonBaseProjections: number
  // Calculated-ratio-only: numerator/denominator sums so the total rows can compute a weighted
  // average SUM(numerator) / SUM(denominator) instead of averaging per-client ratios.
  metric1NumeratorActuals?: number
  metric1DenominatorActuals?: number
  metric1NumeratorProjections?: number
  metric1DenominatorProjections?: number
  metric1LastPeriod?: {
    period: string
    actual: number | null
    projection: number | null
    projectionAsOfDate: string | null
    comparisonBaseActual: number | null
    comparisonBaseProjection: number | null
    difference: number
    differencePercentage: number
    percentRevenue: number
    projectionPercentRevenue: number
  }
  metric1LastPeriodActual?: number
  metric1LastPeriodProjection?: number
  metric1LastPeriodComparisonBaseActual?: number
  metric1LastPeriodComparisonBaseProjection?: number
  metric1LastPeriodDifference?: number
  metric1LastPeriodDifferencePercentage?: number
  metric1LastPeriodPercentRevenue?: number
  metric1LastPeriodProjectionPercentRevenue?: number
  // Calculated-ratio-only: last-period numerator/denominator for the weighted Last-Month totals.
  metric1LastPeriodNumeratorActuals?: number
  metric1LastPeriodDenominatorActuals?: number
  metric1LastPeriodNumeratorProjection?: number
  metric1LastPeriodDenominatorProjection?: number
  metric2Name: string
  metric2: number
  metric2Projection: number
  metric2Difference: number
  metric2DifferencePercentage: number
  metric2ProjectionTooltip?: string
  metric2ActualsTooltip?: string
  metric2PercentRevenue: number
  metric2ProjectionPercentRevenue: number
  metric2ComparisonBaseActuals: number
  metric2ComparisonBaseProjections: number
  // See metric1NumeratorActuals/metric1DenominatorActuals.
  metric2NumeratorActuals?: number
  metric2DenominatorActuals?: number
  metric2NumeratorProjections?: number
  metric2DenominatorProjections?: number
  metric2LastPeriod?: {
    period: string
    actual: number | null
    projection: number | null
    comparisonBaseActual: number | null
    comparisonBaseProjection: number | null
  }
}

export interface IFinancialsComparisonData {
  data: IFinancialsComparison[]
  industries: string[]
  totals: {
    totalCount: number
    metric1Total: number
    metric1ProjectionTotal: number
    metric1DifferenceTotal: number
    metric1DifferencePercentageTotal: number
    metric1PercentRevenueTotal: number
    metric1ProjectionPercentRevenueTotal: number
    metric2Total: number
    metric2ProjectionTotal: number
    metric2DifferenceTotal: number
    metric2DifferencePercentageTotal: number
    metric2PercentRevenueTotal: number
    metric2ProjectionPercentRevenueTotal: number
    metric1LastPeriodActualTotal?: number
    metric1LastPeriodProjectionTotal?: number
    metric1LastPeriodDifferenceTotal?: number
    metric1LastPeriodDifferencePercentageTotal?: number
    metric1LastPeriodPercentRevenueTotal?: number
    metric1LastPeriodProjectionPercentRevenueTotal?: number
  }
}

export const RISK_METRIC_OPTIONS: {
  value: keyof IRiskRatingDetail
  label: string
  type: string
}[] = [
  {
    value: 'riskRating',
    label: 'Risk Rating',
    type: 'number',
  },
  {
    value: 'lineTurn',
    label: 'Line Turn',
    type: 'number',
  },
  {
    value: 'cashBurn',
    label: 'Cash Burn T3M',
    type: 'number',
  },
  {
    value: 'cashBurnT6m',
    label: 'Cash Burn T6M',
    type: 'number',
  },
  {
    value: 'cashBurnT12m',
    label: 'Cash Burn T12M',
    type: 'number',
  },
  {
    value: 'grossMarginRollingAvg',
    label: '3M Gross Margin',
    type: 'percent',
  },
  {
    value: 'yoyNetRevGrowth',
    label: 'YoY Net Revenue',
    type: 'percent',
  },
  {
    value: 'percentApLess_60',
    label: '% AP < 60',
    type: 'percent',
  },
  {
    value: 'grossRevenue_6MonthRollingAvg',
    label: '6M Gross Revenue',
    type: 'amount',
  },
  {
    value: 'ebitda_6MonthRollingAvg',
    label: '6M EBITDA',
    type: 'amount',
  },
  {
    value: 'currentRatio',
    label: 'Current Ratio',
    type: 'number',
  },
  {
    value: 'percentArLess_60',
    label: '% AR < 60',
    type: 'percent',
  },
  {
    value: 'arTop_5Conc',
    label: 'AR Top 5 Conc.',
    type: 'percent',
  },

  {
    value: 'dilution',
    label: 'Dilution',
    type: 'percent',
  },
  {
    value: 'weeksOfSupply',
    label: 'Weeks of Supply',
    type: 'number',
  },
]

export const RISK_METRIC_UNDERLYING_DATA: {
  value: keyof IRiskRatingRawData
  label: string
  type: string
  originalValue: keyof IRiskRatingDetail
}[] = [
  {
    value: 'averageCollections',
    label: 'T3M Collections',
    type: 'amount',
    originalValue: 'lineTurn',
  },
  {
    value: 'averageLoanBalance',
    label: '3M Avg Loan Balance',
    type: 'amount',
    originalValue: 'lineTurn',
  },
  {
    value: 'avgEbitda',
    label: 'Avg EBITDA',
    type: 'amount',
    originalValue: 'cashBurn',
  },
  {
    value: 'cashAndCashEquivalents',
    label: 'Cash on Hand',
    type: 'amount',
    originalValue: 'cashBurn',
  },
  {
    value: 't3mNetRevenue',
    label: 'T3M Net Revenue',
    type: 'amount',
    originalValue: 'grossMarginRollingAvg',
  },
  {
    value: 't3mGrossProfit',
    label: 'T3M Gross Profit',
    type: 'amount',
    originalValue: 'grossMarginRollingAvg',
  },
  {
    value: 'trailingSixNetRev',
    label: 'Trailing 6M Net Revenue',
    type: 'amount',
    originalValue: 'yoyNetRevGrowth',
  },
  {
    value: 'pyTrailingSixNetRev',
    label: 'PY trailing 6M Net Revenue',
    type: 'amount',
    originalValue: 'yoyNetRevGrowth',
  },
  {
    value: 'totalAp',
    label: 'Total AP',
    type: 'amount',
    originalValue: 'percentApLess_60',
  },
  {
    value: 'ap0To60Days',
    label: 'AP 0-60 Days',
    type: 'amount',
    originalValue: 'percentApLess_60',
  },
  {
    value: 'totalCurrentAssets',
    label: 'Total Current Assets',
    type: 'amount',
    originalValue: 'currentRatio',
  },
  {
    value: 'totalCurrentLiabilities',
    label: 'Total Current Liabilities',
    type: 'amount',
    originalValue: 'currentRatio',
  },
  {
    value: 'totalGrossAr',
    label: 'Total Gross AR',
    type: 'amount',
    originalValue: 'percentArLess_60',
  },
  {
    value: 'ar1To60Days',
    label: 'AR 1-60 Days',
    type: 'amount',
    originalValue: 'percentArLess_60',
  },
  {
    value: 'totalGrossAr',
    label: 'Total Gross AR',
    type: 'amount',
    originalValue: 'arTop_5Conc',
  },
  {
    value: 't3mGrossRevenue',
    label: 'T3M Gross Revenue',
    type: 'amount',
    originalValue: 'dilution',
  },
  {
    value: 't3mNetRevenue',
    label: 'T3M Net Revenue',
    type: 'amount',
    originalValue: 'dilution',
  },
  {
    value: 't12mCogs',
    label: 'T12M COGS',
    type: 'amount',
    originalValue: 'weeksOfSupply',
  },
  {
    value: 'inventory',
    label: 'Inventory',
    type: 'amount',
    originalValue: 'weeksOfSupply',
  },
]

export const RISK_METRIC_UNDERLYING_DATA_WATCHLIST: {
  value: keyof IRiskRatingRawData
  label: string
  type: string
  originalValue: keyof IRiskRatingDetail
}[] = [
  {
    value: 'previousRiskRating',
    label: 'Previous Risk Rating',
    type: 'number',
    originalValue: 'riskRatingChange',
  },
  {
    value: 'lineTurn',
    label: 'Line Turn',
    type: 'number',
    originalValue: 'lineTurn',
  },
  {
    value: 'cashBurn',
    label: 'Cash Burn T3M',
    type: 'number',
    originalValue: 'cashBurn',
  },
  {
    value: 'cashBurnT6m',
    label: 'Cash Burn T6M',
    type: 'number',
    originalValue: 'cashBurnT6m',
  },
  {
    value: 'cashBurnT12m',
    label: 'Cash Burn T12M',
    type: 'number',
    originalValue: 'cashBurnT12m',
  },
  {
    value: 'grossMarginRollingAvg',
    label: '3M Gross Margin',
    type: 'percent',
    originalValue: 'grossMarginRollingAvg',
  },
  {
    value: 'percentApLess_60',
    label: '% AP Under 60',
    type: 'percent',
    originalValue: 'percentApLess_60',
  },
  {
    value: 'ebitda_6MonthRollingAvg',
    label: '6M EBITDA',
    type: 'amount',
    originalValue: 'ebitda_6MonthRollingAvg',
  },
  {
    value: 'currentRatio',
    label: 'Current Ratio',
    type: 'number',
    originalValue: 'currentRatio',
  },
  {
    value: 'percentArLess_60',
    label: '% AR Under 60',
    type: 'percent',
    originalValue: 'percentArLess_60',
  },
  {
    value: 'dilution',
    label: 'Dilution',
    type: 'percent',
    originalValue: 'dilution',
  },
  {
    value: 'arTop_5Conc',
    label: 'AR Top 5 Conc',
    type: 'percent',
    originalValue: 'arTop_5Conc',
  },
  {
    value: 'weeksOfSupply',
    label: 'WoS',
    type: 'number',
    originalValue: 'weeksOfSupply',
  },
  {
    value: 'yoyNetRevGrowth',
    label: 'YoY Net Rev',
    type: 'percent',
    originalValue: 'yoyNetRevGrowth',
  },
  ...RISK_METRIC_UNDERLYING_DATA,
  {
    value: 'inventoryWoS',
    label: 'WoS',
    type: 'number',
    originalValue: 'inventoryHealthRating',
  },
  {
    value: 'daysSinceLandedGt180',
    label: 'Days Since Landed > 180',
    type: 'amount',
    originalValue: 'inventoryHealthRating',
  },
  {
    value: 'daysUntilExpirationLt90',
    label: 'Days Until Expiration < 90',
    type: 'amount',
    originalValue: 'inventoryHealthRating',
  },
  {
    value: 'salesAndOnHandVariance',
    label: 'Sales vs On Hand Variance',
    type: 'percent',
    originalValue: 'inventoryHealthRating',
  },
  {
    value: 'maxSalesMonths',
    label: 'Max Sales Months',
    type: 'number',
    originalValue: 'inventoryHealthRating',
  },
]

export interface IClientCashFlow {
  header: string
  financialsType: FinancialsType
  recordDate: string
  mostRecentCash: number
  avgOperatingCashFlow: number
  monthsOfCashOnHandOperatingCashFlow: number | string
  monthsOfCashOnHandEBITDA: number | string
  avgEBITDA: number
  avgNetIncome: number
  changeInAccountsReceivableNet: number
  changeInInventory: number
  changeInPrepaidExpenses: number
  changeInAccountsPayable: number
  changeInAccruedExpenses: number
  changeInTaxesPayable: number
  changeInOtherShortTermDebt: number
  changeInOtherLongTermDebt: number
  changeInDwightFundingLoan: number
  changeInNetPpe: number
  changeInAdditionalPaidInCapital: number
  changeInConvertibleNotesDebt: number
  changeInConvertibleNotesEquity: number
  changeInOther: number
  changeInCash: number
  beginingCash: number
  endingCash: number
}

export interface IClientCashFlowData {
  asOfDate: string
  numberOfMonthLast: number
  minDateLast: string
  maxDateLast: string
  numberOfMonthNext: number
  minDateNext: string
  maxDateNext: string
  summary: { [key in FinancialsType]: IClientCashFlow } | {}
  headers: string[]
  data: IClientCashFlow[]
}

export interface IInventoryHealth {
  clientName: string
  recordDate: string
  weeksOfSupply: number | null
  daysSinceLandedGt180Pct: number | null
  daysUntilExpirationLt90Pct: number | null
  avgSalesMonthsPctOfMaxMonths: number | null
  salesAndOnHandVariance: number | null
  weeksOfSupplyRating: number | null
  daysSinceLandedRating: number | null
  daysUntilExpirationRating: number | null
  avgSalesMonthsRating: number | null
  salesOnHandVarianceRating: number | null
  industry: string | null
  inventoryRating: number | null
  totalWeeklySales: number | null
  totalValue: number | null
  weightedSalesMonths: number | null
  maxSalesMonths: number | null
  daysSinceLandedGt180: number | null
  daysUntilExpirationLt90: number | null
  createdAt: string
  updatedAt: string
}

export interface IInventoryHealthData {
  data: {
    recordDate: string
    inventoryHealth: IInventoryHealth
    portfolioAverages: IInventoryHealth
  }[]
}

export const INVENTORY_HEALTH_METRIC_OPTIONS: {
  value: keyof IInventoryHealth
  score: keyof IInventoryHealth
  label: string
  type: string
}[] = [
  {
    value: 'inventoryRating',
    score: 'inventoryRating',
    label: 'Inventory Health Rating',
    type: 'number',
  },
  {
    score: 'weeksOfSupplyRating',
    value: 'weeksOfSupply',
    label: 'Weeks of Supply',
    type: 'number',
  },
  {
    value: 'daysSinceLandedGt180Pct',
    score: 'daysSinceLandedRating',
    label: 'Days Since Landed > 180',
    type: 'percent',
  },
  {
    value: 'daysUntilExpirationLt90Pct',
    score: 'daysUntilExpirationRating',
    label: 'Days Until Expiration < 90',
    type: 'percent',
  },
  {
    value: 'avgSalesMonthsPctOfMaxMonths',
    score: 'avgSalesMonthsRating',
    label: 'Inv. with Max Months of Sales',
    type: 'percent',
  },
  {
    value: 'salesAndOnHandVariance',
    score: 'salesOnHandVarianceRating',
    label: 'Sales vs On Hand Variance',
    type: 'number',
  },
]

export const INVENTORY_HEALTH_METRIC_UNDERLYING_DATA: {
  value: keyof IInventoryHealth
  originalValue: keyof IInventoryHealth
  label: string
  type: string
}[] = [
  {
    value: 'totalWeeklySales',
    originalValue: 'weeksOfSupply',
    label: 'Total Weekly Sales',
    type: 'amount',
  },
  {
    value: 'totalValue',
    originalValue: 'weeksOfSupply',
    label: 'Total Inventory',
    type: 'amount',
  },
  {
    value: 'daysSinceLandedGt180',
    originalValue: 'daysSinceLandedGt180Pct',
    label: 'Days Since Landed > 180 ($)',
    type: 'amount',
  },
  {
    value: 'totalValue',
    originalValue: 'daysSinceLandedGt180Pct',
    label: 'Total Inventory',
    type: 'amount',
  },
  {
    value: 'daysUntilExpirationLt90',
    originalValue: 'daysUntilExpirationLt90Pct',
    label: 'Days Until Expiration < 90 ($)',
    type: 'amount',
  },
  {
    value: 'totalValue',
    originalValue: 'daysUntilExpirationLt90Pct',
    label: 'Total Inventory',
    type: 'amount',
  },
  {
    value: 'maxSalesMonths',
    originalValue: 'avgSalesMonthsPctOfMaxMonths',
    label: 'Max Sales Months',
    type: 'number',
  },
  {
    value: 'weightedSalesMonths',
    originalValue: 'avgSalesMonthsPctOfMaxMonths',
    label: 'Avg Sales Months',
    type: 'number',
  },
]

export enum ClientBannerType {
  Custom = 'custom',
  Holiday = 'holiday',
  PendingCashDominion = 'pendingCashDominion',
}

export enum ClientBannerCustomAction {
  ShowModal = 'showModal',
}

export interface IClientBannerData {
  action?: ClientBannerCustomAction
  label?: string
  cancelLabel?: string
  isBeta?: boolean
  imageKey?: string
  data?: {
    title?: string
    cash_dominion_customers?: { customer: string; amount: string }[]
  }
}

export interface IClientBanner {
  id: string
  type: ClientBannerType
  title: string
  description: string
  icon: string | null
  color: string
  link: string | null
  startDate: string
  endDate: string
  holidayId: string | null
  data: IClientBannerData
  // Resolved from `data.imageKey` on read; never persisted
  imageUrl?: string | null
  clientName?: string | null
  clientInfo?: { id: string } | null
  manualOverrideAt?: string | null
  createdAt?: string
}

export interface IClientBannerImageUpload {
  attachmentSrc: string
  fileName: string
  contentType: string
}

// Border / tint color applied to banners created from the portfolio settings UI so
// they render as prominently as the auto-generated holiday banners
export const CLIENT_BANNER_DEFAULT_COLOR = '#0066F5'

export const CLIENT_BANNER_DEFAULT_ACTION_LABEL = 'Take me there'
export const CLIENT_BANNER_DEFAULT_CANCEL_LABEL = 'Cancel'

// Portal paths navigate in-app; anything else must be an explicit http(s) URL. A
// protocol-relative `//host` is external, same rule as isInternalAtlasUrl
export const isAllowedClientBannerLink = (link: string): boolean =>
  (link.startsWith('/') && !link.startsWith('//')) || /^https?:\/\/\S+$/i.test(link)

// Admins type links like "dwightfunding.com/x"; without a scheme the browser would
// resolve them relative to the portal
export const normalizeClientBannerLink = (link: string): string => {
  const trimmed = link.trim()
  if (!trimmed || trimmed.startsWith('/') || /^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
    return trimmed
  }
  return `https://${trimmed}`
}

export interface IClientLoanLedgerWire {
  isParticipantWire: boolean
  item: IAccountActivity | IParticipationWire
}

export interface IClientLoanLedger {
  id: string
  clientName: string
  recordDate: string
  loanBalance: number
  loanBalancePrev: number
  funds_in: number
  funds_out: number
  fees: number
  interestRateDaily: number
  interestAmountDaily: number
  dwightExposure: number
  total_fees: number
  /**
   * Line utilization as of this ledger day (revolver ending balance ÷ net availability),
   * expressed as a decimal. Null when net availability is unknown at that date.
   */
  utilization?: number | null
  participationLoanExposure: IParticipationLoanExposure[]
  wires: IClientLoanLedgerWire[]
}

export interface IClientLoanLedgerData {
  data: IClientLoanLedger[]
  totalCount: number
}

// Colors reference theme-aware CSS variables (defined in client tokens.scss) so the
// score heatmap adapts to light/dark mode. Consumed only as client inline styles.
export const RISK_RATING_COLOR_MAPPING = [
  {
    min: 6,
    max: null,
    colors: { backgroundColor: 'var(--risk-rating-6-bg)', color: 'var(--risk-rating-6-text)' },
  },
  {
    min: 5,
    max: 6,
    colors: { backgroundColor: 'var(--risk-rating-5-bg)', color: 'var(--risk-rating-5-text)' },
  },
  {
    min: 4,
    max: 5,
    colors: { backgroundColor: 'var(--risk-rating-4-bg)', color: 'var(--risk-rating-4-text)' },
  },
  {
    min: 3,
    max: 4,
    colors: { backgroundColor: 'var(--risk-rating-3-bg)', color: 'var(--risk-rating-3-text)' },
  },
  {
    min: 2,
    max: 3,
    colors: { backgroundColor: 'var(--risk-rating-2-bg)', color: 'var(--risk-rating-2-text)' },
  },
  {
    min: null,
    max: 2,
    colors: { backgroundColor: 'var(--risk-rating-1-bg)', color: 'var(--risk-rating-1-text)' },
  },
  {
    min: null,
    max: null,
    colors: {
      backgroundColor: 'var(--risk-rating-null-bg)',
      color: 'var(--risk-rating-null-text)',
    },
  },
]

export enum ClientIndustries {
  Apparel = 'Apparel',
  Beauty = 'Beauty',
  Beverages = 'Beverages',
  ChildBaby = 'Child/Baby',
  Food = 'Food',
  Home = 'Home',
  Lifestyle = 'Lifestyle',
  Other = 'Other',
  Outdoor = 'Outdoor',
  PetProducts = 'Pet Products',
}

export enum CollectionsReconciliationGroupBy {
  Month = 'Month',
  Parent = 'Parent',
  Customer = 'Customer',
}

export interface ICollectionsReconciliationOptions {
  /** Span the data actually covers — advisory, not a hard picker bound. */
  maxDate: string
  minDate: string
  /** Client's contract date, the hard lower bound for the date picker. */
  contractDate: string | null
}

export interface ICollectionsReconciliation {
  label: string
  entity_id?: string
  implied_collections: number
  ar_gl_payments: number
  actual_collections: number
  ar_gl_invoices: number
  ar_gl_dilutive_credits: number
  ar_gl_other: number
  ar_gl_dilution: number
  actual_dilution: number
  dilution_change: number
}

export interface ICollectionsReconciliationData {
  data: ICollectionsReconciliation[]
  totals: ICollectionsReconciliation[]
  totalItems: number
}

export interface ISalesChannelDistributionOptions {
  maxDate: string | null
  minDate: string | null
}

export interface ISalesChannelDistribution {
  label: string
  customer_deposits: number
  gross_revenue: number
  net_revenue: number
  ar_gl_invoices: number
  ar_gl_payments: number
  ar_gl_dilutive_credits: number
  dilution: number
  total_items?: number
}

export interface ISalesChannelDistributionData {
  data: ISalesChannelDistribution[]
  totals: ISalesChannelDistribution[]
  totalItems: number
}

export type MonthlyExhibitFieldType = 'amount' | 'percent'

/** One metric of a months-as-columns exhibit; `null` = no data for that measure that month. */
export interface IMonthlyExhibitRow {
  key: string
  label: string
  fieldType: MonthlyExhibitFieldType
  formula?: string
  values: (number | null)[]
  total: number | null
}

export interface IMonthlyExhibit {
  /** Month labels, oldest first, aligned with every row's `values`. */
  headers: string[]
  rows: IMonthlyExhibitRow[]
}

export interface ICustomerInvoicingRow {
  label: string
  entity_id: string | null
  ar_gl_invoices: number | null
  credit_memos: number | null
  bank_collections: number | null
  credit_memo_dilution: number | null
  invoice_to_cash_gap: number | null
}

export interface ICustomerInvoicingVsCollections {
  /** The window actually used — the last 12 months of data when none was requested. */
  startDate: string | null
  endDate: string | null
  rows: ICustomerInvoicingRow[]
  allOthers: ICustomerInvoicingRow | null
  total: ICustomerInvoicingRow
  unmatchedBankCollections: number | null
  processorPayouts: number | null
}

export enum BankDataCoverage {
  Full = 'full',
  Partial = 'partial',
  None = 'none',
}

/** How a customer collection is grouped: by the payer's entity type, or Uncategorized when unmapped. */
export enum CollectionsChannel {
  Wholesale = 'Wholesale',
  D2C = 'D2C',
  Uncategorized = 'Uncategorized',
}

export interface ICustomerCollectionsMonth {
  /** First day of the month, `YYYY-MM-DD`. */
  month: string
  customerCollections: number
  /** Null when the P&L has no actuals for the month yet. */
  netRevenue: number | null
  /**
   * Revenue expected to be collected this month: the wholesale share of last month's net revenue
   * plus the D2C share of this month's. Null until the share is entered, or when a month it needs
   * has no P&L.
   */
  expectedCollections: number | null
  variance: number | null
  uncategorisedInflows: number
  totalInflows: number
  bankCoverage: BankDataCoverage
}

export interface ICustomerCollectionsLine {
  label: string
  /** Keyed by `ICustomerCollectionsMonth.month`. */
  amounts: Record<string, number>
  total: number
}

export interface ICustomerCollectionsEntityLine extends ICustomerCollectionsLine {
  /** Null for the Unmapped line. */
  entityId: string | null
}

export interface ICustomerCollectionsCategoryLine extends ICustomerCollectionsLine {
  /** Largest first, with inflows mapped to no entity as a final Unmapped line. */
  entities: ICustomerCollectionsEntityLine[]
}

export interface ICustomerCollectionsTotals {
  customerCollections: number
  netRevenue: number | null
  expectedCollections: number | null
  variance: number | null
  uncategorisedInflows: number
  totalInflows: number
}

export interface ICustomerCollectionsData {
  minDate: string | null
  maxDate: string | null
  bankDataMinDate: string | null
  bankDataMaxDate: string | null
  /** Wholesale share of net revenue as a fraction; null until the underwriter enters it. */
  wholesaleShare: number | null
  months: ICustomerCollectionsMonth[]
  /** Customer collections as Wholesale, D2C and Uncategorized, in that order. */
  channels: ICustomerCollectionsLine[]
  /** Every inflow by its confirmed bank-transaction category, Uncategorized last. */
  categories: ICustomerCollectionsCategoryLine[]
  totals: ICustomerCollectionsTotals
}

export enum TermLoanOveradvanceType {
  TermLoan = 'Term Loan',
  Overadvance = 'Overadvance',
  // Equipment Loans are term loans with an Equipment deal type. They surface
  // only in the client-page Term Loans & Overadvances table, never the
  // portfolio-wide one.
  EquipmentLoan = 'Equipment Loan',
}

export interface ITermLoanOveradvanceItem {
  clientName: string
  clientId: string
  startDate: string
  endDate: string | null
  outstandingBalance: number
  effectiveBalance: number
  type: TermLoanOveradvanceType
  id: string
}

export interface ITermLoansOveradvancesData {
  data: ITermLoanOveradvanceItem[]
  totals: {
    totalItems: number
    totalOutstandingBalance: number
    totalEffectiveBalance: number
  }
}

export const ABL_SENTINEL = 'ABL'

export enum AmendmentEntityType {
  Overadvance = 'Overadvance',
  TermLoan = 'Term Loan',
  Participation = 'Participation',
  Abl = 'ABL',
}

/** Cast-free narrower for SalesforceStage (see common/helpers/enumGuard). */
export const asSalesforceStage = enumNarrow(SalesforceStage).as

/** Cast-free narrower for ClientInfoStatus (see common/helpers/enumGuard). */
export const asClientInfoStatus = enumNarrow(ClientInfoStatus).as
